import React from "react";
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Stack,
  Image,
  Flex,
  Button,
  VStack,
  HStack,
  Center,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import WorldMap from "../components/WorldMap";
import Timeline from "../components/Timeline";

import e2eproj from "../assets/e2eproj.jpg";
import scalable from "../assets/scalable.jpg";
import costeffective from "../assets/costeffective.jpg";
import cuttingedge from "../assets/cuttingedge.jpg";
import bannerOverview from "../assets/aboutus.jpg";
import bannerWhy from "../assets/whyhorison.jpg";
import bannerProcesses from "../assets/processes.jpg";
import ProcessFlow from "../components/ProcessFlow";

import solar24 from "../assets/savemoney.jpg";
import savemoney from "../assets/solar24-7.jpg";
import relibility from "../assets/relibility.jpg";
import projects from "../assets/about2.jpg";
import horisondeliver from "../assets/horisondeliver.jpg";

const MotionBoxC = motion(Box);
const MotionBox = motion(Box);

const MotionText = motion(Box);

const AnimatedParagraph = ({ text }) => {
  return (
    <Box as="p">
      {text.split(" ").map((word, index) => (
        <MotionText
          as="span"
          key={index}
          fontSize={{ base: "sm", md: "md" }}
          lineHeight="base"
          fontWeight="medium"
          color="gray.700"
          display="inline-block"
          mr={1}
          variants={{
            hidden: { opacity: 0, y: 10 },
            visible: {
              opacity: 1,
              y: 0,
              transition: { duration: 0.4, delay: index * 0.05 },
            },
          }}
          initial="hidden"
          animate="visible"
        >
          {word}
        </MotionText>
      ))}
    </Box>
  );
};

export default function AboutUs() {
  // Overview highlights
  const highlights = [
    {
      title: "End-to-End Project Expertise",
      desc: "We handle everything from concept to commissioning — including design, engineering, procurement, installation, and maintenance.",
      img: e2eproj, // replace with relevant image
    },
    {
      title: "Scalable & Custom Solutions",
      desc: "Whether it's a rooftop system or utility-scale solar, our designs are tailored and future-ready to grow with your energy needs.",
      img: scalable, // replace with relevant image
    },
    {
      title: "Cost-Effective Engineering",
      desc: "We optimize for performance and budget, delivering maximum value without compromising on quality or sustainability.",
      img: costeffective, // replace with relevant image
    },
    {
      title: "Cutting-Edge Software & Design Tools",
      desc: "We utilize the latest industry-leading software for system design, simulation, and performance analysis to ensure precision and efficiency at every stage.",
      img: cuttingedge, // <-- add a 4th image in your assets folder
    },
  ];

  const whyHorisun = [
    {
      title: "Service with Vision",
      desc: "We work closely with our clients to ensure every project excels in execution, durability, and operational efficiency — delivering long-term value and maximum return on investment.",
      img: solar24,
    },
    {
      title: "Cost-Optimized, Quality-Driven",
      desc: "We design efficient, cost-effective systems that lower project expenses and boost ROI — without compromising on performance or durability.",
      img: savemoney,
    },
    {
      title: "Highly Skilled Team",
      desc: "Our dynamic team of experienced and driven professionals brings fresh energy and deep expertise to every project — consistently delivering innovative, high-quality solutions.",
      img: relibility,
    },
    {
      title: "Advanced Engineering Excellence",
      desc: "Utilizing the newest technology and intelligent software, we provide optimized, error-free engineering tailored for maximum efficiency",
      img: horisondeliver,
    },
  ];

  // Processes
  const processesList = [
    {
      title: "Client Consultation & Energy Assessment",
      desc: "We first understand the client’s energy consumption patterns (industrial, commercial, residential, or utility-scale) and assess sustainability goals and financial constraints.",
    },
    {
      title: "Site Survey & Feasibility Study",
      desc: "Our team conducts on-site visits: analyzing roof space, land availability, shading, orientation, and evaluates grid connectivity, local DISCOM guidelines, and load profile. We also analyze project ROI, payback period, and viability under conditions like weather and pollution.",
    },
    {
      title: "System Design & Engineering",
      desc: "We design system architecture (on-grid, off-grid, hybrid, or open access) and select technology: Mono/Poly PV, string vs central inverters, battery storage if required. Designs comply with MNRE and CEA standards.",
    },
    {
      title: "Regulatory Approvals & Permits",
      desc: "Our team submits applications to DISCOM for Net Metering, Grid Synchronization, etc., ensuring compliance with State Electricity Regulatory Commission (SERC) norms.",
    },
    {
      title: "Procurement & Project Management",
      desc: "We source Tier-1 equipment (MNRE-approved modules, BIS-certified inverters) and plan logistics around climate, local festivals, and transport constraints, maintaining supply chain and vendor timelines.",
    },
    {
      title: "Installation & Commissioning",
      desc: "Mobilizing experienced EPC teams or subcontractors, we ensure safety (IS standards & OSHA) and quality compliance, then perform grid synchronization and final handover with DISCOM.",
    },
    {
      title: "Monitoring, Maintenance & Support",
      desc: "We deploy remote monitoring systems (SCADA or cloud platforms) and offer AMC/O&M contracts, real-time dashboards, SLAs, and periodic reporting.",
    },
  ];

  // Scroll function for navbar
  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const yOffset = -80; // Adjust if navbar is sticky
      const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <Box bg="#F5FAF6" color="#305D24">
      {/* Overview Section */}
      <Box id="overview">
        {/* Hero Section */}

        <Box>
          <Box
            position="relative"
            shadow="xl"
            mb={10}
            overflow="hidden"
            display="flex"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            minH={{ base: "250px", md: "350px" }} // smaller height
            py={{ base: 8, md: 0 }}
          >
            {/* Background Image */}
            <Box
              position="absolute"
              top={0}
              left={0}
              width="100%"
              height="100%"
              bgImage={`url(${bannerOverview})`}
              bgSize="cover"
              bgPosition="center"
              filter="brightness(0.6) blur(1.5px)"
              zIndex={0}
            />

            {/* Content */}
            <Box
              position="relative"
              zIndex={1}
              color="white"
              px={{ base: 4, md: 10 }}
              py={{ base: 6, md: 0 }}
              w="100%"
              maxW="4xl"
              mx="auto"
              borderRadius="lg"
            >
              <MotionBoxC
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ type: "spring", stiffness: 80 }}
              >
                <Heading
                  fontSize={{ base: "2xl", md: "4xl" }}
                  mb={4}
                  textShadow="2px 2px 6px rgba(0,0,0,0.8)"
                >
                  About HoriSun
                </Heading>
              </MotionBoxC>

              <MotionBoxC
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 80 }}
              >
                <Text
                  fontSize={{ base: "md", md: "xl" }}
                  lineHeight="tall"
                  color="white"
                  textShadow="1px 1px 4px rgba(0,0,0,0.7)"
                  fontWeight="bold"
                >
                  Your Trusted Partner For All-in-one Renewable Energy Solutions
                </Text>
              </MotionBoxC>
            </Box>
          </Box>

          {/* Sub Content Section with Highlighted Background */}
          <Box
            position="relative"
            rounded="2xl"
            shadow="xl"
            overflow="hidden"
            display="flex"
            flexDirection={{ base: "column", md: "row" }}
            alignItems="stretch"
            mb={10}
            bgColor="beige"
          >
            {/* Text Section */}
            <Box
              position="relative"
              zIndex={1}
              px={{ base: 6, md: 12 }}
              py={{ base: 8, md: 12 }}
              w={{ base: "100%", md: "50%" }}
              display="flex"
              flexDirection="column"
              justifyContent="center"
            >
              <VStack spacing={5} align="start">
                <AnimatedParagraph text="HoriSun is a one-stop platform providing turnkey solutions to Solar Developers, EPCs & Installers. Started in 2022 as an engineering company, we have broadened our services to include SCM, EPCM, and PMC. Our services are available at any project stage: from Pre-Sale & early planning to construction, installation, and commissioning." />

                <AnimatedParagraph text="Our team comprises young, energetic, and experienced professionals dedicated to delivering top-notch services. We differentiate ourselves mainly in two ways:" />

                <AnimatedParagraph text="Process: Automating design processes to enable faster turnaround, reduced errors, and lower costs." />

                <AnimatedParagraph text="Customer Orientation: Our engineers strictly follow project norms to maximize value while keeping costs efficient. Clients work directly with dedicated account managers focused on their needs." />
              </VStack>
            </Box>

            {/* Image Section */}
            <Box
              w={{ base: "100%", md: "50%" }}
              display="flex"
              alignItems="center"
              justifyContent="center"
              py={{ base: 6, md: 12 }}
              pr={{ base: 4, md: 12 }}
            >
              <Box
                position="relative"
                w="100%"
                h={{ base: "240px", md: "100%" }}
                maxH={{ md: "450px" }}
                bgImage={`url(${projects})`}
                bgSize="cover"
                bgPosition="center"
                filter="brightness(0.85)"
                borderRadius={{ base: "none", md: "2xl" }}
                boxShadow="md"
              />
            </Box>
          </Box>
        </Box>

        {/* Key Strengths Section */}
        <Box py={10}>
          <Heading
            color="#519C36"
            mb={8}
            textAlign="center"
            fontSize={{ base: "2xl", md: "5xl" }}
          >
            Our Key Strengths
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8}>
            {highlights.map((item) => (
              <MotionBox
                key={item.title}
                bg="white"
                rounded="2xl"
                shadow="xl"
                overflow="hidden"
                border="2px solid #F5FAF6"
                whileHover={{
                  scale: 1.05,
                  shadow: "2xl",
                  borderColor: "#F6A11A",
                }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.6 }}
              >
                <Image
                  src={item.img}
                  alt={item.title}
                  objectFit="cover"
                  w="100%"
                  h="200px"
                />
                <Stack p={6}>
                  <Heading fontSize="xl" color="#305D24" mb={2}>
                    {item.title}
                  </Heading>
                  <Text color="#519C36" fontSize="sm">
                    {item.desc}
                  </Text>
                </Stack>
              </MotionBox>
            ))}
          </SimpleGrid>
        </Box>
      </Box>

      {/* Why HoriSun Section */}
      <Box id="why" py={10}>
        <Box position="relative" shadow="xl" mb={10} overflow="hidden">
          <Box
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            bgImage={`url(${bannerWhy})`}
            bgSize="cover"
            bgPosition="center"
            filter="brightness(0.5)"
            zIndex={0}
          />
          <Box
            position="relative"
            zIndex={1}
            color="white"
            p={{ base: 10, md: 16 }}
            textAlign="center"
          >
            <MotionBox
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ type: "spring", stiffness: 80 }}
            >
              <Heading
                fontSize={{ base: "3xl", md: "5xl" }}
                mb={4}
                textShadow="2px 2px 4px rgba(0,0,0,0.7)"
              >
                Why Choose HoriSun
              </Heading>
            </MotionBox>
            <MotionBox
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 80 }}
            >
              <Text
                fontWeight="bold"
                fontSize={{ base: "md", md: "lg" }}
                maxW="3xl"
                mx="auto"
                textShadow="1px 1px 3px rgba(0,0,0,0.6)"
              >
                At HoriSun Renewables, our focus is on delivering reliable,
                innovative, and sustainable energy solutions. Here’s why clients
                trust us for their solar projects.
              </Text>
            </MotionBox>
          </Box>
        </Box>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10}>
          {whyHorisun.map((item, index) => (
            <MotionBox
              key={item.title}
              position="relative"
              rounded="2xl"
              p={6}
              shadow="xl"
              border="1px solid #E6F3EA"
              overflow="hidden"
              whileHover={{
                scale: 1.05,
                y: -5,
                boxShadow: "0px 12px 30px rgba(0,0,0,0.1)",
                borderColor: "#F6A11A",
              }}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, type: "spring", stiffness: 80 }}
              minH="280px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {/* Background image with blur */}
              <Image
                src={item.img}
                alt={item.title}
                objectFit="cover"
                position="absolute"
                top={0}
                left={0}
                w="100%"
                h="100%"
                zIndex={0}
                filter="brightness(0.6) blur(2px)" // subtle blur
              />

              {/* Content overlay */}
              <VStack
                spacing={4}
                align="center"
                textAlign="center"
                p={6}
                rounded="xl"
                zIndex={1} // ensures text stays above blurred bg
              >
                <Heading
                  fontSize={{ base: "md", md: "lg" }}
                  color="white"
                  textTransform="none"
                  letterSpacing="wide"
                >
                  {item.title}
                </Heading>
                <Text
                  fontSize={{ base: "sm", md: "md" }}
                  color="white"
                  fontWeight="bold"
                >
                  {item.desc}
                </Text>
              </VStack>
            </MotionBox>
          ))}
        </SimpleGrid>
      </Box>

      {/* Our Processes Section */}
      <Box id="processes" py={10} bg="#F5FAF6">
        {/* Hero Banner */}
        <Box position="relative" shadow="xl" mb={10} overflow="hidden">
          <Box
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            bgImage={`url(${bannerProcesses})`}
            bgSize="cover"
            bgPosition="center"
            filter="brightness(0.5)"
            zIndex={0}
          />
          <Box
            position="relative"
            zIndex={1}
            color="white"
            p={{ base: 10, md: 16 }}
            textAlign="center"
          >
            <MotionBox
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ type: "spring", stiffness: 80 }}
            >
              <Heading
                fontSize={{ base: "2xl", md: "5xl" }}
                mb={4}
                textShadow="2px 2px 4px rgba(0,0,0,0.7)"
              >
                Our Processes
              </Heading>
            </MotionBox>
            <MotionBox
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 80 }}
            >
              <Text
                fontWeight="bold"
                fontSize={{ base: "md", md: "lg" }}
                maxW="2xl"
                mx="auto"
                lineHeight="tall"
                textShadow="1px 1px 3px rgba(0,0,0,0.6)"
              >
                At HoriSun, we follow a proven, end-to-end process that ensures
                seamless execution of renewable energy projects—from initial
                consultation to long-term performance monitoring. Our approach
                combines deep engineering expertise, regulatory knowledge, and a
                commitment to sustainability. Whether you're a business,
                government entity, or institution, we design and deliver
                tailored clean energy solutions that meet your unique goals.
              </Text>
            </MotionBox>
          </Box>
        </Box>

        <ProcessFlow />

        {/* Detailed Steps */}

        <SimpleGrid
          columns={{ base: 1, md: 1 }}
          spacing={10}
          px={4}
          py={8}
          maxW="100%"
          mx="auto"
          bgGradient="linear(to-r, #f0f4f8, #e0f2f1)"
        >
          {processesList.map((item, index) => (
            <MotionBox
              key={item.title}
              bg="#FAFAFA"
              rounded="3xl"
              p={6}
              shadow="lg"
              border="1px solid #D0E2DC"
              transition={{ ease: "easeInOut", duration: 0.3 }}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              whileHover={{
                scale: 1.03,
                shadow: "2xl",
                borderColor: "#F6A11A",
              }}
            >
              <HStack align="start" spacing={4}>
                {/* Symbol on left */}
                <Box
                  w={10}
                  h={10}
                  mt={1} // slight top margin to align with text
                  bg="#F6A11A"
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  color="white"
                  fontWeight="bold"
                  fontSize="xl"
                  userSelect="none"
                >
                  ✓
                </Box>

                {/* Text Section */}
                <VStack spacing={2} align="start" textAlign="left" flex="1">
                  <Heading
                    fontSize="xl"
                    color="#31653B"
                    textTransform="none"
                    letterSpacing="wide"
                    fontWeight="bold"
                    fontFamily="'Montserrat', sans-serif"
                  >
                    {item.title}
                  </Heading>
                  <Text
                    fontSize="md"
                    color="#2F4F2F"
                    lineHeight="1.6"
                    fontFamily="'Raleway', sans-serif"
                  >
                    {item.desc}
                  </Text>
                </VStack>
              </HStack>
            </MotionBox>
          ))}
        </SimpleGrid>
      </Box>

      {/* Timeline & Global Map */}
      <Timeline />
      <Box py={5}>
        <WorldMap />
      </Box>

      <Box >
        <Flex justify="center" py={5}>
          <Button
            bg="#FBBF24"
            color="#065F46"
            size="lg"
            as="a"
            href="/contact"
            _hover={{ bg: "#F59E0B" }}
          >
            Get in Touch
          </Button>
        </Flex>
      </Box>
    </Box>
  );
}
